import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { useSEO } from "@/lib/useSEO";
import { langAlternates, langCanonical } from "@/lib/langSeo";
import NewsletterDisclosure from "@/components/NewsletterDisclosure";
import MyStoicOsBlock from "@/components/MyStoicOsBlock";
import VirtueGuideSection from "@/components/VirtueGuideSection";
import FoundersWall from "@/components/FoundersWall";
import { HOME_ENTRY, HOME_ENTRY_IMAGES } from "@/content/homeEntry";
import { STOICOS_CARDS, pickLang, stoicosCardUrl } from "@/data/stoicosCards";

const serif = { fontFamily: "'Cormorant Garamond','EB Garamond',Georgia,serif" };
const track = (name) => { try { window.gtag && window.gtag("event", name); } catch (_) {} };
const Lines = ({ text, className, style }) => (
  <p className={className} style={style}>{text.split("\n").map((l, i) => <span key={i}>{l}{i < text.split("\n").length - 1 && <br />}</span>)}</p>
);

function Scene({ img, priority, children, testId, frieze, contentClass = "" }) {
  return (
    <section data-testid={testId} className="relative min-h-[100svh] md:min-h-screen flex items-end md:items-center overflow-hidden">
      <picture>
        {img.mobile && <source media="(min-width: 768px)" srcSet={img.url} />}
        <img src={img.mobile ? img.mobile.url : img.url} alt={img.alt} loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : "auto"} decoding="async"
          className={`absolute inset-0 w-full h-full object-cover ${img.mobile ? "object-right md:object-center" : "object-center"}`} />
      </picture>
      {frieze && (
        <div className="absolute inset-0 pointer-events-none" aria-hidden="false">
          {/* Mirrors the object-cover geometry of the image so the motto stays glued to the stone frieze. */}
          <div style={{
            position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
            aspectRatio: img.aspectRatio, minWidth: "100%", minHeight: "100%", containerType: "size",
          }}>
            <span
              data-testid="frieze-text"
              style={{
                position: "absolute", left: "50%", top: "6.4%", transform: "translate(-50%,-50%)",
                fontFamily: "'Cormorant Garamond','EB Garamond',Georgia,serif",
                fontWeight: 600, letterSpacing: "0.34em", whiteSpace: "nowrap",
                paddingLeft: "0.34em",
                fontSize: `min(3.3cqh, ${(92 / frieze.length).toFixed(2)}vw)`,
                color: "rgba(158,127,78,0.95)",
                textShadow: "0 1px 1px rgba(255,240,200,0.35), 0 -1px 2px rgba(0,0,0,0.55)",
              }}
            >
              {frieze}
            </span>
          </div>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/85 via-[#050505]/25 to-transparent" aria-hidden="true" />
      {img.mobile && (
        <div className="absolute inset-0 md:hidden bg-gradient-to-r from-[#050505]/70 via-[#050505]/25 to-transparent" aria-hidden="true" />
      )}
      <div className="absolute inset-0 hidden md:block bg-gradient-to-r from-[#050505]/55 via-transparent to-transparent" aria-hidden="true" />
      <div className={`relative w-full max-w-4xl mx-auto px-6 md:px-10 pb-16 md:pb-0 pt-32 motion-safe:animate-[fadeInUp_1.2s_ease_both] ${contentClass}`}>
        {children}
      </div>
    </section>
  );
}

function OptionCard({ label, selected, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} aria-pressed={selected}
      className={`w-full text-left min-h-[52px] px-5 py-4 border transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/70 ${
        selected ? "border-gold bg-gold/10 text-bone" : "border-white/12 bg-white/[0.02] text-bone/75 hover:border-gold/40 hover:text-bone"}`}>
      <span style={serif} className="text-lg md:text-xl leading-snug">{label}</span>
    </button>
  );
}

function DiscoverBlock() {
  const { lang, t } = useT();
  const linkCls = "inline-block min-h-[48px] px-7 py-3.5 bg-gold text-ink text-[11px] tracking-[0.24em] uppercase font-semibold leading-relaxed hover:bg-gold/90 transition-colors text-center";
  const cardCls = "border border-white/10 bg-[#0b0b0b] p-7 md:p-9 flex flex-col gap-5";
  const teaser = ["dichotomy-of-control", "four-virtues", "the-stoic-path"]
    .map((s) => STOICOS_CARDS.find((c) => c.slug === s))
    .filter(Boolean);
  return (
    <section className="max-w-5xl mx-auto px-6 md:px-10 pt-24 md:pt-32" data-testid="home-discover">
      <h2 style={serif} className="text-3xl md:text-4xl text-bone text-center">{t("home.discover.title")}</h2>
      <p className="mt-5 text-center text-bone/60 font-light leading-relaxed">
        {t("home.discover.intro1")}<br />{t("home.discover.intro2")}
      </p>
      <div className="mt-14 grid md:grid-cols-2 gap-6">
        <div className={cardCls} data-testid="discover-card-agora">
          <h3 style={serif} className="text-2xl text-bone">{t("home.discover.agora.title")}</h3>
          <p className="text-bone/65 font-light leading-relaxed text-sm flex-1">{t("home.discover.agora.body")}</p>
          <Link to="/agora" data-testid="discover-agora-cta" onClick={() => track("home_entry_path_selected")} className={linkCls}>
            {t("home.discover.agora.cta")}
          </Link>
        </div>
        <div className={cardCls} data-testid="discover-card-journal">
          <h3 style={serif} className="text-2xl text-bone">{t("home.discover.journal.title")}</h3>
          <p className="text-bone/65 font-light leading-relaxed text-sm flex-1">{t("home.discover.journal.body")}</p>
          <Link to="/stoic-journal" data-testid="discover-journal-cta" onClick={() => track("home_entry_path_selected")} className={linkCls}>
            {t("home.discover.journal.cta")}
          </Link>
        </div>
      </div>

      {/* StoicOs Cards — discovery entry point */}
      <div className="mt-6 border border-white/10 bg-[#0b0b0b] p-7 md:p-10 md:flex md:items-center md:gap-10" data-testid="home-cards-teaser">
        <div className="flex justify-center md:justify-start shrink-0" aria-hidden="true">
          {teaser.map((c, i) => (
            <img
              key={c.slug}
              src={stoicosCardUrl(c)}
              alt=""
              loading="lazy"
              width={c.w}
              height={c.h}
              className={`w-24 sm:w-28 md:w-32 h-auto border border-gold/25 shadow-xl ${i === 1 ? "z-10 -rotate-1" : i === 0 ? "rotate-[-4deg] translate-y-1" : "rotate-[4deg] translate-y-1"} ${i > 0 ? "-ml-8 sm:-ml-10" : ""}`}
            />
          ))}
        </div>
        <div className="mt-7 md:mt-0 text-center md:text-left">
          <p className="text-[10px] tracking-[0.3em] uppercase text-gold/70">{t("home.cards.eyebrow")}</p>
          <h3 style={serif} className="mt-2 text-2xl text-bone">{t("home.cards.title")}</h3>
          <p className="mt-3 text-bone/65 font-light leading-relaxed text-sm max-w-xl">{t("home.cards.body")}</p>
          <Link
            to="/stoic-training-cards"
            data-testid="home-cards-teaser-cta"
            onClick={() => track("home_entry_path_selected")}
            aria-label={`${t("home.cards.cta")} — ${pickLang(teaser[0]?.alt || "", lang)}`}
            className="mt-5 inline-block text-[11px] tracking-[0.24em] uppercase font-semibold text-gold border-b border-gold/50 pb-1 hover:border-gold transition-colors"
          >
            {t("home.cards.cta")} →
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function HomeEntry() {
  const { lang, t } = useT();
  const c = HOME_ENTRY[lang] || HOME_ENTRY.nl;
  const [choice, setChoice] = useState(null);       // A..F — session only, never persisted
  const [followUp, setFollowUp] = useState(null);   // index of follow-up answer
  const followRef = useRef(null);
  const adviceRef = useRef(null);

  useSEO({
    title: "StoicOs.ai — Practical Stoic Wisdom for Inner Freedom",
    description: "Learn to think clearly, face reality and live with wisdom, courage, justice and self-control through the practical disciplines of Stoicism.",
    canonical: langCanonical("/", lang),
    alternates: langAlternates("/"),
  });

  const pick = (key) => {
    setChoice(key); setFollowUp(null);
    track(choice === null ? "home_entry_first_answer_selected" : "home_entry_started");
    setTimeout(() => followRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
  };
  const pickFollow = (i) => {
    setFollowUp(i); track("home_entry_followup_completed");
    setTimeout(() => adviceRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
  };
  const branch = choice ? c.branches[choice] : null;
  const showReaction = choice === "F" || (branch && followUp !== null);

  return (
    <main data-testid="home-entry" className="bg-ink">
      {/* Scène 1 — Voor de poort */}
      <Scene img={HOME_ENTRY_IMAGES.gate} priority testId="scene-gate">
        <p className="text-[12px] md:text-sm tracking-[0.28em] uppercase text-gold/90">{c.scene1.pre}</p>
        <h1 style={serif} className="mt-6 text-4xl sm:text-5xl lg:text-6xl text-bone leading-[1.05]">{c.scene1.h1}</h1>
        <p className="mt-7 max-w-xl text-bone/80 font-light leading-[1.9] text-base md:text-lg">
          {c.scene1.body.map((l, i) => <span key={i}>{l}<br /></span>)}
        </p>
        <div className="mt-14 md:mt-20 flex items-center gap-3 text-bone/45">
          <ChevronDown size={15} strokeWidth={1.25} className="motion-safe:animate-bounce" aria-hidden="true" />
          <span className="text-[10px] tracking-[0.26em] uppercase">{c.scene1.scrollHint}</span>
        </div>
      </Scene>

      {/* Scène 2 — Door de poort */}
      <Scene img={HOME_ENTRY_IMAGES.courtyard} testId="scene-courtyard" frieze={t("home.frieze")} contentClass="md:translate-y-[9vh]">
        <h2 style={serif} className="text-3xl md:text-5xl text-bone">{c.scene2.title}</h2>
        <p className="mt-6 max-w-xl text-bone/80 font-light leading-[1.9] text-base md:text-lg">
          {c.scene2.intro[0]}<br />{c.scene2.intro[1]}
        </p>
        <p className="mt-8 text-bone/70 font-light">{c.scene2.line}</p>
        <div style={serif} className="mt-8 space-y-3 text-2xl md:text-3xl text-bone/90">
          {c.scene2.triad.map((l, i) => <p key={i}>{l}</p>)}
        </div>
      </Scene>

      {/* Scène 3 — Ontvangst door Hipparchia */}
      <Scene img={HOME_ENTRY_IMAGES.welcome} testId="scene-hipparchia">
        <h2 style={serif} className="text-[2.7rem] leading-tight md:text-6xl text-bone">{c.scene3.title}</h2>
        <p style={serif} className="mt-4 md:mt-5 max-w-[58%] md:max-w-none text-[1.35rem] md:text-3xl text-gold/90 italic leading-snug md:leading-normal">{c.scene3.name}</p>
        <div className="mt-6 md:mt-7 max-w-[58%] md:max-w-xl space-y-4 md:space-y-5 text-bone/80 font-light leading-[1.75] md:leading-[1.9] text-[0.9rem] md:text-lg">
          <p>{c.scene3.p1}</p>
          <p style={serif} className="text-lg md:text-2xl text-bone not-italic">
            {c.scene3.p2.map((l, i) => <span key={i}>{l}<br /></span>)}
          </p>
          <p>{c.scene3.p3}</p>
          <p style={serif} className="pt-2 text-lg md:text-2xl italic text-gold/90 not-italic">
            <span className="italic">{c.scene3.promptLead}</span><br />
            <span className="italic">{c.scene3.prompt}</span>
          </p>
        </div>
      </Scene>

      {/* Sectie 4 — direct de interactieve vragenreeks */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="home-question-1">
        <h2 className="sr-only">{c.q1.question}</h2>
        <div className="grid gap-3" role="group" aria-label={c.q1.question}>
          {Object.entries(c.q1.options).map(([k, label]) => (
            <OptionCard key={k} label={label} selected={choice === k} onClick={() => pick(k)} testId={`q1-option-${k}`} />
          ))}
        </div>
      </section>

      {/* Sectie 5 — vervolgvraag + reactie */}
      <div ref={followRef} aria-live="polite">
        {branch && branch.question && (
          <section className="max-w-3xl mx-auto px-6 md:px-10 pb-20" data-testid="home-followup">
            <h3 style={serif} className="text-2xl md:text-3xl text-bone">{branch.question}</h3>
            <div className="mt-8 grid gap-3" role="group" aria-label={branch.question}>
              {branch.options.map((o, i) => (
                <OptionCard key={i} label={o} selected={followUp === i} onClick={() => pickFollow(i)} testId={`followup-option-${i}`} />
              ))}
            </div>
          </section>
        )}
        {showReaction && (
          <section className="max-w-3xl mx-auto px-6 md:px-10 pb-20 motion-safe:animate-[fadeInUp_0.9s_ease_both]" data-testid="hipparchia-reaction">
            <div className="border-l-2 border-gold/50 pl-6 md:pl-8">
              <p className="text-[10px] tracking-[0.28em] uppercase text-gold/80 mb-4">Hipparchia</p>
              <Lines text={c.branches[choice].reaction} className="text-bone/85 font-light leading-[2] text-lg md:text-xl" style={serif} />
            </div>
            <button onClick={() => { setChoice(null); setFollowUp(null); }} data-testid="change-answer"
              className="mt-8 text-[10px] tracking-[0.2em] uppercase text-bone/40 hover:text-gold transition-colors min-h-[44px]">
              {c.changeAnswer}
            </button>
          </section>
        )}
      </div>

      {/* Sectie 6 — Persoonlijk advies */}
      <div ref={adviceRef}>
        {showReaction && (
          <section className="max-w-3xl mx-auto px-6 md:px-10 pb-24 motion-safe:animate-[fadeInUp_0.9s_ease_both]" data-testid="personal-advice" aria-live="polite">
            <h2 style={serif} className="text-3xl md:text-4xl text-bone">{c.advice.title}</h2>
            {choice === "F" ? (
              <Lines text={c.advice.soft} className="mt-7 text-bone/75 font-light leading-[1.95] text-base md:text-lg" />
            ) : (
              <>
                <Lines text={c.advice.standard} className="mt-7 text-bone/75 font-light leading-[1.95] text-base md:text-lg" />
                <p className="mt-6 text-bone/75 font-light">{c.advice.bulletsIntro}</p>
                <ul className="mt-3 space-y-2 text-bone/70 font-light list-none">
                  {c.advice.bullets.map((b, i) => (
                    <li key={i} className="pl-5 relative"><span className="absolute left-0 text-gold" aria-hidden="true">—</span>{b}</li>
                  ))}
                </ul>
                <p className="mt-6 text-gold/90 font-light">{c.advice.free}</p>
              </>
            )}
          </section>
        )}
      </div>

      {/* Sectie 7 — My StoicOs als centrale toegang */}
      <MyStoicOsBlock />

      {/* Sectie 7a — Virtue Guide: de mobiele metgezel (officiële lancering) */}
      <VirtueGuideSection />

      {/* Sectie 7a2 — Founders Wall of Honor */}
      <FoundersWall />

      {/* Sectie 7b — Eerst meer ontdekken: Agora & Stoic Journal */}
      <DiscoverBlock />

      {/* Sectie 7c — Nieuwsbrief als laatste CTA van de homepage */}
      <NewsletterDisclosure variant="home" />

      {/* Sectie 8 — Korte afsluiting */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 pb-32 text-center" data-testid="home-closing">
        <p className="text-bone/65 font-light leading-[2] text-base md:text-lg">
          {c.closing.lines.map((l, i) => <span key={i}>{l}<br /></span>)}
        </p>
        <p style={serif} className="mt-10 text-2xl md:text-3xl italic text-gold">{c.closing.emphasis}</p>
        <h2 style={serif} className="mt-8 text-4xl md:text-5xl text-bone">{c.closing.big}</h2>
        <p className="mt-4 text-[11px] tracking-[0.3em] uppercase text-bone/45">{c.closing.sub}</p>
      </section>
    </main>
  );
}
